import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Login } from '../components/Login';
import { ProtectedRoute } from './ProtectedRoute';
import { ComunidadGuard } from './ComunidadGuard';
import { AppShell } from '../components/Layout/AppShell';
import { OnboardingRedirect } from '../components/OnboardingRedirect';
import { HomeView } from '../pages/home/HomeView';
import { SessionsPage } from '../pages/sessions/SessionsPage';
import { CalendarPage } from '../pages/calendar/CalendarPage';
import { CaminoInicialPage } from '../pages/camino-inicial/CaminoInicialPage';
import { PatientsPage } from '../pages/patients/PatientsPage';
import { ComunidadPage } from '../pages/comunidad/ComunidadPage';
import { ComunidadAdminPage } from '../pages/comunidad/ComunidadAdminPage';
import { NewsletterArchivePage } from '../pages/comunidad/NewsletterArchivePage';
import { NewsArchivePage } from '../pages/comunidad/NewsArchivePage';
import { FormacionesDictadasPage } from '../pages/comunidad/FormacionesDictadasPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { Derivaciones } from '../pages/Derivaciones';
import { NoAgendamientoEmbed } from '../pages/NoAgendamientoEmbed';
import { VacacionesEmbed } from '../pages/VacacionesEmbed';
import { MeetEmbebida } from '../pages/MeetEmbebida';
import { CleanPage } from '../pages/CleanPage';
import { CertActionPage } from '../pages/CertActionPage';
import { FarewellEmbed } from '../pages/FarewellEmbed';
import { KnowledgeBasePreviewPage } from '../pages/kb-preview/KnowledgeBasePreviewPage';
import { PwaPreviewRoute } from '../pwa';
import { MeetPage } from '../pages/meet/MeetPage';
import { InstalledTabletPushGate } from '../pwa/InstalledTabletPushGate';
/** Redirects legacy paths preserving query params */
const LegacyRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Root & backwards-compat redirect */}
      <Route path="/" element={<OnboardingRedirect />} />
      <Route path="/psychologist" element={<OnboardingRedirect />} />

      <Route path="/login" element={<Login />} />
      <Route path="/clean" element={<CleanPage />} />
      {/* Página pública para aprobar/rechazar certificados desde el email al equipo */}
      <Route path="/cert-action" element={<CertActionPage />} />
      {/* Preview público (sin login) del contenido de la knowledge base — usado por el backoffice */}
      <Route path="/kb-preview" element={<KnowledgeBasePreviewPage />} />
      {/* Landing de la PWA mobile para revisarla desde un celular sin instalar; en desktop redirige a la app — ver src/pwa */}
      <Route path="/pwa" element={<PwaPreviewRoute />} />

      {/* Protected layout shell */}
      <Route
        element={
          <ProtectedRoute requirePsychologist>
            {/* Tablet con la app instalada: misma plataforma, pero con el bloqueo por notificaciones (no hace nada en el resto). */}
            <InstalledTabletPushGate>
              <AppShell />
            </InstalledTabletPushGate>
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<HomeView />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/sessions/meet" element={<MeetEmbebida />} />
        {/* Videollamada embebida de la psicóloga (psi-video): dentro del layout para poder sumar herramientas de la
            plataforma (agenda, agendar) al costado de la llamada. Se llega por su URL (/meet/{token}, su link fijo). */}
        <Route path="/meet/:token" element={<MeetPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/calendar/time-off" element={<VacacionesEmbed />} />
        {/* Slug directo de la vista de Agenda (semanal/mensual/diaria) — ver src/routes/slugs.ts */}
        <Route path="/calendar/:view" element={<CalendarPage />} />
        {/* Solo NZ activa / en transición (O viniendo de NZ) / recién graduada sin confirmar
            egreso — gate y visibilidad de tab en AppShell (useNzJourneyStatus.journeyMode) */}
        <Route path="/camino-inicial" element={<CaminoInicialPage />} />
        <Route path="/patients" element={<PatientsPage />} />
        {/* Lanzamiento acotado: Comunidad solo visible impersonando (ver ComunidadGuard) */}
        <Route element={<ComunidadGuard />}>
          <Route path="/comunidad" element={<ComunidadPage />} />
          <Route path="/comunidad/admin" element={<ComunidadAdminPage />} />
          <Route path="/comunidad/newsletter" element={<NewsletterArchivePage />} />
          <Route path="/comunidad/novedades/:feedSlug" element={<NewsArchivePage />} />
          {/* Solo para quien dictó al menos una formación: si no, la pantalla vuelve a /comunidad */}
          <Route path="/comunidad/dictadas" element={<FormacionesDictadasPage />} />
          <Route path="/comunidad/dictadas/:seccion" element={<FormacionesDictadasPage />} />
        </Route>
        <Route path="/patients/derivation" element={<Derivaciones />} />
        <Route path="/patients/no-schedule" element={<NoAgendamientoEmbed />} />
        {/* Slug directo del filtro de estado de Pacientes — ver src/routes/slugs.ts */}
        <Route path="/patients/:status" element={<PatientsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/farewell" element={<FarewellEmbed />} />
        {/* Slugs directos de las tabs de Perfil y de sus sub-tabs de Métricas — ver src/routes/slugs.ts */}
        <Route path="/profile/metricas/:subtab" element={<ProfilePage />} />
        {/* Secciones internas de Liquidación y PSI+ (ej. /profile/metricas/liquidacion/pago) — ver METRICS_SECTIONS */}
        <Route path="/profile/metricas/:subtab/:section" element={<ProfilePage />} />
        <Route path="/profile/:tab" element={<ProfilePage />} />
      </Route>

      {/* Legacy redirects — preserve query params */}
      <Route path="/derivaciones" element={<LegacyRedirect to="/patients/derivation" />} />
      <Route path="/noagendamiento" element={<LegacyRedirect to="/patients/no-schedule" />} />
      <Route path="/vacaciones" element={<LegacyRedirect to="/calendar/time-off" />} />
      <Route path="/meetEmbebida" element={<LegacyRedirect to="/sessions/meet" />} />
      {/* "Camino Inicial" se mudó de sub-tab de Perfil→Métricas a tab de nivel superior */}
      <Route path="/profile/metricas/camino-inicial" element={<LegacyRedirect to="/camino-inicial" />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export const routePaths = {
  home: '/home',
  login: '/login',
  sessions: '/sessions',
  calendar: '/calendar',
  caminoInicial: '/camino-inicial',
  patients: '/patients',
  comunidad: '/comunidad',
  profile: '/profile',
} as const;

export type RoutePath = typeof routePaths[keyof typeof routePaths];
